import { useEffect, useState } from 'react';

import { isNotEmpty, useForm } from '@mantine/form';

import { useTranslation } from 'react-i18next';

import { searchTendersPreview } from '../../../../../shared/api/magellan/tender';
import type { StreamFormValues } from '../../../../../shared/entities/StreamFilterSettings';
import { removeConsecutiveDuplicates } from '../../../../../shared/utils/array';
import {
  useCreateStreamMutation,
  useSearchTendersPreviewQuery,
  useStreamQuery,
  useUpdateStreamMutation,
} from './streams.hook';

export type SearchTendersPreviewQueryKey = [
  typeof searchTendersPreview.name,
  Omit<StreamFormValues, 'name' | 'streamSectionId'>,
];

const DEFAULT_FORM_VALUES: StreamFormValues = {
  name: '',
  status: ['OPEN'],
  keywords: [],
  includedCPVs: [],
  excludedCPVs: [],
  includeTendersWithoutCPV: true,
  includeTendersWithoutDepartment: true,
  buyerIds: [],
  locations: [],
  afterPublicationDate: undefined,
  beforePublicationDate: undefined,
  afterResponseDeadlineDate: undefined,
  beforeResponseDeadlineDate: undefined,
};

export function useStreamForm(streamId?: number) {
  const { t } = useTranslation('stream');
  const { data: stream } = useStreamQuery(streamId);
  const { createStream } = useCreateStream();
  const { updateStream } = useUpdateStream(streamId);

  const form = useForm<StreamFormValues>({
    validateInputOnChange: true,
    initialValues: DEFAULT_FORM_VALUES,
    validate: {
      name: isNotEmpty(),
      status: value =>
        value.length > 0 ? null : t('editStreamForm.selectAtLeastOneStatus'),
    },
  });

  if (stream && !form.isDirty()) {
    form.setValues({
      ...stream.filterSettings,
      name: stream.name,
      streamSectionId: stream.streamSectionId,
    });
  }

  const [queryKeys, setQueryKeys] = useState<SearchTendersPreviewQueryKey[]>(
    [],
  );

  const { name: _, streamSectionId: __, ...filters } = form.values;
  const searchTendersPreviewQuery = useSearchTendersPreviewQuery(filters);

  useEffect(() => {
    if (searchTendersPreviewQuery.data) {
      setQueryKeys(prev => [[searchTendersPreview.name, filters], ...prev]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTendersPreviewQuery.data]);

  return {
    form,
    isFormValid: form.isValid(),
    isFormDirty: form.isDirty(),
    createStream: form.onSubmit(createStream),
    updateStream: form.onSubmit(updateStream),
    searchTendersPreviewQuery,
    queryKeys: removeConsecutiveDuplicates(queryKeys),
  };
}

const useCreateStream = () => {
  const { t } = useTranslation('stream');

  const createStreamMutation = useCreateStreamMutation();
  const createStream = async (values: StreamFormValues) => {
    const { name, streamSectionId, ...filterSettings } = values;

    createStreamMutation.mutate({
      name: name || t('createStream.defaultStreamName'),
      filterSettings,
      streamSectionId: streamSectionId === -1 ? undefined : streamSectionId,
    });
  };

  return { createStream };
};

const useUpdateStream = (streamId: number | undefined) => {
  const updateStreamMutation = useUpdateStreamMutation();
  const updateStream = async (values: StreamFormValues) => {
    const { name, streamSectionId, ...filterSettings } = values;

    if (!streamId) {
      throw new Error('Stream ID is required');
    }

    updateStreamMutation.mutate({
      streamId,
      payload: {
        name,
        filterSettings,
        streamSectionId: streamSectionId === -1 ? undefined : streamSectionId,
      },
    });
  };

  return { updateStream };
};
